import * as FC from "@fluentui/react-components";
import * as FDP from "@fluentui/react-datepicker-compat";
import { TurbineSelector } from "../../services/turbine_service";
import {
    DefDefectionDef,
    DefDefectionDropdown,
} from "../../services/def_defection_service";
import { PartDropdown, idString, mapPart, shortId } from "../../db_utils";
import { useContext, useEffect, useState } from "react";
import { bindDropdownValue, bindInputValue } from "../../form_utils";
import { toDateString, toDateTimeString, yyyymmdd } from "../../date_utils";
import { SimpleGrid } from "../../components/simple_grid";
import { useDrawer } from "../../components/use_drawer";
import { Context } from "../../context";
import { listDefection } from "../../services/defection_service";
import { ViewFrames } from "./view_frames";

// Session review status options for filtering
const SESSION_STATUS_OPTIONS = [
    { value: "", text: "全部" },
    { value: "completed", text: "已完成" },
    { value: "ready_to_release", text: "待发布" },
    { value: "released", text: "已发布" },
    { value: "in_recheck", text: "重新标注中" },
];

export const DatacenterDefectionManagement = () => {
    const { apiClient } = useContext(Context);
    const [filters, setFilters] = useState({});
    const [items, setItems] = useState([]);
    const [statistics, setStatistics] = useState({
        total: 0,
        completed: 0,
        readyToRelease: 0,
        released: 0,
        inRecheck: 0
    });
    const { drawer, openDrawer, closeDrawer } = useDrawer({
        position: "left",
        style: { width: 1000 },
    });
    const v = bindInputValue(null, filters, setFilters);
    const d = bindDropdownValue(null, filters, setFilters);

    const loadDefections = async () => {
        const items = await listDefection(apiClient, filters);
        setItems(items);

        // Calculate statistics based on session status
        const stats = {
            total: items.length,
            completed: 0,
            readyToRelease: 0,
            released: 0,
            inRecheck: 0
        };

        items.forEach(item => {
            const sessionStatus = item.from_frame?.from_raw?.task?.session?.ReviewStatus;
            if (sessionStatus === 'ready_to_release') {
                stats.readyToRelease++;
            } else if (sessionStatus === 'released') {
                stats.released++;
            } else if (sessionStatus === 'in_recheck') {
                stats.inRecheck++;
            } else if (item.from_frame?.from_raw?.task?.status === 'completed') {
                stats.completed++;
            }
        });

        setStatistics(stats);
    };

    useEffect(() => {
        if (!apiClient) {
            return;
        }
        loadDefections();
    }, [apiClient, filters]);
    const columns = [
        FC.createTableColumn({
            columnId: "seq",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>缺陷类型编号</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        <span className="overflow-hidden">
                            {item.label?.seq}
                        </span>
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "leaf_seq",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>叶片编号</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {item.from_frame?.from_raw?.leaf?.seq}
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "turbine_name",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>风机名称</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {item.from_frame?.from_raw?.leaf?.turbine?.name}
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "defection_type",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>缺陷类型</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>{item.label?.name}</FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "part",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>位置</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {mapPart[item.from_frame?.from_raw?.part]?.text}
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "field_name",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>风场名称</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {
                            item.from_frame?.from_raw?.leaf?.turbine?.field_item
                                ?.name
                        }
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "checker",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>巡检负责人</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {item.from_frame?.from_raw?.task?.owner_checker?.name}
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "rechecker",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>缺陷复核人</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {item.from_frame?.from_raw?.task?.owner_rechecker?.name}
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "completed_at",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>巡检时间</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                return (
                    <FC.TableCellLayout>
                        {toDateTimeString(
                            item.from_frame?.from_raw?.task?.check_at,
                        )}
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "session_status",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>任务状态</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                const sessionStatus = item.from_frame?.from_raw?.task?.session?.ReviewStatus;
                let statusText = "已完成";
                let statusColor = FC.tokens.colorPaletteGreenBorder2;

                if (sessionStatus === 'ready_to_release') {
                    statusText = "待发布";
                    statusColor = FC.tokens.colorPaletteLightBlueBackground2;
                } else if (sessionStatus === 'released') {
                    statusText = "已发布";
                    statusColor = FC.tokens.colorNeutralBackground3;
                } else if (sessionStatus === 'in_recheck') {
                    statusText = "重新标注中";
                    statusColor = FC.tokens.colorPaletteMarigoldBackground2;
                }

                return (
                    <FC.TableCellLayout>
                        <FC.Badge
                            appearance="tint"
                            style={{ backgroundColor: statusColor }}
                        >
                            {statusText}
                        </FC.Badge>
                    </FC.TableCellLayout>
                );
            },
        }),
        FC.createTableColumn({
            columnId: "ops",
            renderHeaderCell: () => {
                return <FC.TableCellLayout>操作</FC.TableCellLayout>;
            },
            renderCell: (item) => {
                const onClick = () => {
                    openDrawer({
                        header: "巡检数据",
                        body: <ViewFrames defectionId={item.id} />,
                    });
                };
                return (
                    <FC.TableCellLayout>
                        <FC.Link onClick={onClick}>查看详情</FC.Link>
                    </FC.TableCellLayout>
                );
            },
        }),
    ];
    return (
        <div className="flex flex-col gap-8 p-2">
            {/* Statistics Summary Section */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold mb-3">缺陷统计</h3>
                <div className="flex gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-gray-600">总计:</span>
                        <span className="font-bold text-xl">{statistics.total}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <FC.Badge appearance="tint" style={{ backgroundColor: FC.tokens.colorPaletteGreenBackground2 }}>
                            已完成: {statistics.completed}
                        </FC.Badge>
                    </div>
                    <div className="flex items-center gap-2">
                        <FC.Badge appearance="tint" style={{ backgroundColor: FC.tokens.colorPaletteLightBlueBackground2 }}>
                            待发布: {statistics.readyToRelease}
                        </FC.Badge>
                    </div>
                    <div className="flex items-center gap-2">
                        <FC.Badge appearance="tint" style={{ backgroundColor: FC.tokens.colorNeutralBackground3 }}>
                            已发布: {statistics.released}
                        </FC.Badge>
                    </div>
                    <div className="flex items-center gap-2">
                        <FC.Badge appearance="tint" style={{ backgroundColor: FC.tokens.colorPaletteMarigoldBackground2 }}>
                            重新标注中: {statistics.inRecheck}
                        </FC.Badge>
                    </div>
                </div>
            </div>

            <div className="flex flex-row gap-4">
                <div className="grid grid-cols-3 gap-x-4">
                    <FC.Field label={"缺陷类型编号"}>
                        <FC.Input {...v("defection_seq")} />
                    </FC.Field>
                    <FC.Field label={"叶片编号"}>
                        <FC.Input {...v("leaf_seq")} />
                    </FC.Field>
                    {/*
                    <FC.Field label={"风机编号"}>
                        <FC.Input {...v("turbine_seq")} />
                    </FC.Field>
                    */}
                    <FC.Field label={"风机名称"}>
                        <FC.Input {...v("turbine_name")} />
                    </FC.Field>
                    <FC.Field label={"缺陷类型"}>
                        <DefDefectionDropdown {...d("defection_type")} />
                    </FC.Field>
                    <FC.Field label={"缺陷位置"}>
                        <PartDropdown {...d("defection_part")} />
                    </FC.Field>
                    <FC.Field label={"任务状态"}>
                        <FC.Dropdown {...d("session_status")}>
                            {SESSION_STATUS_OPTIONS.map((option) => (
                                <FC.Option key={option.value} value={option.value}>
                                    {option.text}
                                </FC.Option>
                            ))}
                        </FC.Dropdown>
                    </FC.Field>
                    <FC.Field label={"巡检时间"}>
                        <FDP.DatePicker
                            formatDate={toDateString}
                            onSelectDate={(date) => {
                                setFilters({
                                    ...filters,
                                    check_at: yyyymmdd(date),
                                });
                            }}
                        />
                    </FC.Field>
                </div>
                <div></div>
            </div>

            <SimpleGrid
                items={items}
                columns={columns}
                getRowId={(item) => idString(item.id)}
            />
            {drawer}
        </div>
    );
};
